
import React, {PropTypes, Component} from 'react';
import { Link, browserHistory } from 'react-router';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { SetSummary } from '../../actions';
import * as d3 from 'd3';
import styles from './ForecastData.css';
import withStyles from '../../decorators/withStyles';
import {isUS, xlt} from "../../utils/utils";
import {resolveForecastUnits} from "../../utils/utils";


class ForecastData extends Component {

  constructor(props) {
    super(props);
    this.state = {
      data: {}
    };

    this.margin = {top: 20, right: 20, bottom: 55, left: 75};
    this.width = 660 - this.margin.left - this.margin.right;
    this.height = 350 - this.margin.top - this.margin.bottom;

    this.subtitles = {
      "Multi-year": "Risk of at least one flood from 2016 through each year shown",
      "Single-year": "Risk of at least one flood within each year shown",
      "Basic": "A localized projection",
      "With mild flood": "A \u201Cmild flood\u201D averages once per year",
      "With moderate flood": "A \u201Cmoderate flood\u201D has a roughly 10% chance per year",
      "With extreme flood": "A \u201Cmajor flood\u201D has a roughly 1% chance per year",
    },

    this.projValDesc = {
      "Multi-year": (x, x_unit, y, z, y_unit) => `<var>${Math.round(z * 100)}</var>% multi-year likelihood (<var>2016-${y}</var>) of a flood of <var>${x.toFixed(0)}</var> ${x_unit} or more above current ` +
      `high tide line.`,
      "Single-year": (x, x_unit, y, z, z_unit) => `<var>${Math.round(z * 100)}</var>% single-year likelihood (<var>${y}</var>) of a flood of <var>${x.toFixed(0)}</var> ${x_unit} or more above current ` +
      `high tide line.`,
      "Basic": (x, x_unit, y, z, z_unit) => `<var>${z.toFixed(1)}</var> ${z_unit}`,
      "With mild flood": (x, x_unit, y, z, z_unit) => `<var>${z.toFixed(1)}</var> ${z_unit}`,
      "With moderate flood": (x, x_unit, y, z, z_unit) => `<var>${z.toFixed(1)}</var> ${z_unit}`,
      "With extreme flood": (x, x_unit, y, z, z_unit) => `<var>${z.toFixed(1)}</var> ${z_unit}`,
    };
  }


  summarizeData() {
    const forecastData = this.state.data.forecastData;
    const forecastSummary = this.state.data.forecastSummary;
    const forecastFastLook = this.state.data.forecastFastLook;
    const slr = forecastData["Basic"];
    const level = slr.levels[0];
    const total = forecastData["Multi-year"];
    const total_risk = total.levels.filter(level => (level.water_level == this.props.waterLevel.level && level.water_unit === this.props.waterLevel.unit))[0];

    this.props.dispatch(SetSummary(0, {type: "forecast",
      data: {forecastType: this.props.forecastType,
        level2050: level.yVals[3],
        level2100: level.yVals[8],
        baselineYear: "2012",
        riskYr1: total_risk.xVals[1],
        riskPct1: total_risk.yVals[1],
        riskYr2: total_risk.xVals[3],
        riskPct2: total_risk.yVals[3],
        periodSLR: forecastFastLook.PeriodSLR,
        earliestRecord: forecastFastLook.ANALYSISSTART,
        summaryText: forecastSummary.Summary}
    }));
  }

  async loadData() {
    var self = this;
    const { place, forecastType, stationNum } = this.props;
    if (!place.id) {
      return;
    }
    const slrdb = "&slrdb=true";
    const urls = [
      `/api/forecast/${place.type}/${place.id}/${forecastType}?stationNum=${stationNum}${slrdb}`,
      `/api/forecast/${place.type}/${place.id}/fast-look?stationNum=${stationNum}&columns=HIGHOBS,HIGHYR,RL100,ANALYSISSTART,ANALYSISEND,HILEV,PeriodSLR,RL1,RL10,RL100`,
      `/api/content/summary/${forecastType}?columns=Summary,hardwired12,hardwired36`,
    ];

    const results = await Promise.all(urls.map((url) => d3.json(url)));
    const [ data, forecastFastLook, forecastSummary ] = results;
    var forecastData;
    if (slrdb === "") {
      forecastData = {};
      data.projections.forEach(proj => {
        proj.subprojections.forEach(subproj => {
          forecastData[subproj.name] = subproj;
        });
      });
    } else {
      forecastData = data;
    }

    self.setState({data: {
      forecastData: resolveForecastUnits(forecastData, this.props.waterLevel.unit),
      stnShortName: data.stn_name,
      forecastFastLook,
      forecastSummary,
    }}, self.summarizeData);
  }

  update() {
    if (this.hasData()) {
      this.summarizeData();
    }
  }

  componentDidMount() {
    this.loadData();
  }

  componentDidUpdate(props) {
    if (props.place.id !== this.props.place.id || props.place.type !== this.props.place.type ||
        props.forecastType !== this.props.forecastType || props.stationNum !== this.props.stationNum) {
      this.loadData();
    }

    if (props.waterLevel.level != this.props.waterLevel.level ||
      props.waterLevel.unit !== this.props.waterLevel.unit ||
      props.protection !== this.props.protection) {
      this.update();
    }
  }

  getGraphValues(forecastData, forecastName, waterLevel, textMap) {
    var data = [];
    var p = forecastData.forecastData[forecastName];
    if (typeof p === "undefined") {
      return data;
    }
    var level;
    if (forecastName === "Multi-year" || forecastName === "Single-year") {
      level = p.levels.filter(level => level.water_level == waterLevel.level && level.water_unit === waterLevel.unit);
    } else {
      level = p.levels.filter(level => level.water_unit === waterLevel.unit);
    }
    if (level.length === 1) {
      level = level[0];
    } else {
      return level;
    }

    for (let i=0; i < level.xVals.length; i++) {
      data.push({x: level.xVals[i], y: level.yVals[i],
        text: textMap(waterLevel.level, waterLevel.unit, level.xVals[i], level.yVals[i], waterLevel.unit)});
    }

    return data;
  }

  chartExplain(data, forecastName, stnName) {
    const floodHeights = data.forecastFastLook;
    const explanations = {
      "Total": `Flood height is measured from the 1992 high tide line at <var>${stnName}</var> (mean higher high water from 1983-2001), aligning to this tool\u2019s maps and analysis.`,
      "Annual": `Flood height is measured from the 1992 high tide line at <var>${stnName}</var> (mean higher high water from 1983-2001), aligning to this tool\u2019s maps and analysis.`,
      "Basic": "Sea level rise is relative to a 1992 baseline. Projected values share the same scale as this tool's maps when taken as heights above the 1992 high tide line.",
      "With mild flood": <span>{`Sea level rise is relative to a 1992 baseline. A \u201Cmild flood\u201D is locally defined as `}<var>{parseFloat(floodHeights.RL1).toFixed(1)}</var>{` ft above the high tide line in the year it occurs.`}</span>,
      "With moderate flood": <span>{`Sea level rise is relative to a 1992 baseline. A \u201Cmoderate flood\u201D is locally defined as `}<var>{parseFloat(floodHeights.RL10).toFixed(1)}</var> {`ft above the high tide line in the year it occurs.`}</span>,
      "With extreme flood": <span>{`Sea level rise is relative to a 1992 baseline. A \u201Cmajor flood\u201D is locally defined as`} <var>{parseFloat(floodHeights.RL100).toFixed(1)}</var> {`ft above the high tide line in the year it occurs.`}</span>,
    };

    return explanations[forecastName];
  };

  hasData() {
    return this.state && this.state.data && Object.keys(this.state.data).length > 0;
  }

  render() {
    if (!this.hasData()) {
      return null;
    }
    const waterLevel = this.props.waterLevel;
    const graphTypes = [
      {text: <span>Multi-year risk of flooding above <var>{waterLevel.level}</var> <var>{waterLevel.unit}</var></span>, value: "Multi-year"},
      {text: <span>Single-year risk of flooding above <var>{waterLevel.level}</var> <var>{waterLevel.unit}</var></span>, value: "Single-year"},
      {text: <span>Projected sea level rise</span>, value: "Basic"},
      {text: <span>Projected sea level rise + mild flood level</span>, value: "With mild flood"},
      {text: <span>Projected sea level rise + moderate flood level</span>, value: "With moderate flood"},
    ];
    if (isUS(this.props.place.id)) {
      graphTypes.push({text: `Projected sea level rise + major flood level`, value: "With extreme flood"})
    }

    return (
      <div className="Forecast main">
        <div>{React.Children.map(this.props.children,
          child => React.cloneElement(child, {
            data: this.state.data,
            getGraphValues: this.getGraphValues,
            chartExplain: this.chartExplain,
            textMap: this.projValDesc,
            graphTypes,
            subtitles: this.subtitles,
            ...this.props
          }))}
        </div>
      </div>
    );
  }
}

function selectProps(state) {
  return {
    place: state.place,
    placeName: state.placeName,
    stationNum: state.stationNum,
    stations: state.stations,
    forecastName: state.forecastName,
    forecastType: state.forecastType,
    metadata: state.forecastMetadata,
    waterLevel: state.waterLevel,
    modalVisible: state.modalVisible,
    isEmbed: state.isEmbed,
  }
}

export default compose(
  withStyles(styles),
  connect(selectProps),
)(ForecastData);
